import { type Lap } from '../../domain/lap/Lap'
import { analyzeTrackZones } from '../zones/TrackZoneAnalysis'
import {
  type ZoneDetectionConfig,
  type ZoneAnalysisResult,
  type TrackZone,
  type ZoneLapData,
} from '../zones/TrackZoneTypes'
import { DEFAULT_ZONE_DETECTION_CONFIG } from '../config/zoneDetection'
import { type ZoneVariancePoint } from './zoneDisplay/referencePointConsistency/consistencyGradient'
import { getDisplayLaps } from './keyLapSelection'

export interface SessionAnalysisOptions {
  excludedLapIds: Set<string>
  config?: Partial<ZoneDetectionConfig>
  sessionLabels?: Map<string, string>
}

export interface SessionAnalysisResult {
  zones: TrackZone[]
  focusZones: TrackZone[]
  improveZones: TrackZone[]
  solidZones: TrackZone[]
  zoneLapData: Map<string, ZoneLapData[]>
  displayLaps: (zoneId: string) => ZoneLapData[]
  trackGradient: ZoneVariancePoint[] | null
}

export function filterExcludedLaps(
  domainLaps: Lap[],
  excludedLapIds: Set<string> | undefined
): Lap[] {
  if (!excludedLapIds || excludedLapIds.size === 0) return domainLaps
  return domainLaps.filter((lap) => !excludedLapIds.has(lap.id))
}

function emptyZoneResult(): ZoneAnalysisResult {
  return {
    zones: [],
    focusZones: [],
    improveZones: [],
    solidZones: [],
    zoneLapData: new Map<string, ZoneLapData[]>(),
    trackStats: {
      totalZones: 0,
      highVarianceZones: 0,
      overallConsistency: 0,
      totalTimeLossMs: 0,
      trackLength: 0,
    },
    trackGradient: null,
  }
}

export function detectSessionZones(
  filteredLaps: Lap[],
  allDomainLaps: Lap[],
  overrides?: Partial<ZoneDetectionConfig>,
  excludedLapIds: Set<string> = new Set(),
  sessionLabels?: Map<string, string>
): ZoneAnalysisResult {
  if (filteredLaps.length < 2) return emptyZoneResult()
  const config: ZoneDetectionConfig = {
    ...DEFAULT_ZONE_DETECTION_CONFIG,
    ...overrides,
  }
  return analyzeTrackZones(
    filteredLaps,
    config,
    allDomainLaps,
    allDomainLaps,
    excludedLapIds,
    sessionLabels
  )
}

export function runSessionAnalysis(
  domainLaps: Lap[],
  options: SessionAnalysisOptions
): SessionAnalysisResult {
  const { excludedLapIds, config: overrides, sessionLabels } = options

  const filtered = filterExcludedLaps(domainLaps, excludedLapIds)

  return sessionAnalysisResult(
    detectSessionZones(
      filtered,
      domainLaps,
      overrides,
      excludedLapIds,
      sessionLabels
    )
  )
}

export function sessionAnalysisResult(
  zoneResult: ZoneAnalysisResult
): SessionAnalysisResult {
  return {
    zones: zoneResult.zones,
    focusZones: zoneResult.focusZones,
    improveZones: zoneResult.improveZones,
    solidZones: zoneResult.solidZones,
    zoneLapData: zoneResult.zoneLapData,
    trackGradient: zoneResult.trackGradient,
    displayLaps: (zoneId: string) => {
      const data = zoneResult.zoneLapData.get(zoneId) ?? []
      const zone =
        zoneResult.focusZones.find((z) => z.id === zoneId) ??
        zoneResult.zones.find((z) => z.id === zoneId)
      const setAsideIds = new Set(
        zone?.consistencyPercent?.setAside?.lapIds ?? []
      )
      return getDisplayLaps(data, zone?.variance.primaryIssue, setAsideIds)
    },
  }
}
